import React, { useState } from 'react';
import axios from 'axios';
import Apiurl from '../../Helpers/apiUrl';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import UpdateIcon from '@mui/icons-material/Update';
import { useNavigate } from 'react-router-dom';

const getToken = () => localStorage.getItem('u_token') || '';

/**
 * DraftDialog
 * Props:
 *   isOpen      – boolean
 *   onClose     – fn
 *   draftId     – string|null  (null = never saved)
 *   onDraftId   – fn(id)       (called when a new draft is saved, so parent stores the id)
 *   getPayload  – fn()         (returns the full quotation state to save)
 */

export default function DraftDialog({ isOpen, onClose, draftId, onDraftId, getPayload, setDraftId }) {
  const [loadingAction, setLoadingAction] = useState(null); // null | 'update' | 'new'
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const isLoading = loadingAction !== null;
  const headers = { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' };

  // ── Save as new draft ────────────────────────────────────────────────────────
  const saveNew = async () => {
    setLoadingAction('new'); setError('');
    try {
      const payload = getPayload();
      const { data } = await axios.post(`${Apiurl}project/draft/add`, payload, { headers });
      const id = data?.project_id || data?.data?.project_id;
      const uid = data?.uid || data?.data?.uid||'';
      if (id) {
        onDraftId(String(id));
        setDraftId(String(uid));
      }
      setTimeout(() => {
        onClose();
        navigate('/user/draft');
      }, 100);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save draft. Please try again.');
    } finally {
      setLoadingAction(null);
    }
  };

  // ── Update existing draft ────────────────────────────────────────────────────
  const updateExisting = async () => {
    if (!draftId) return;
    setLoadingAction('update'); setError('');
    try {
      const payload = { ...getPayload(), draftId };
      await axios.post(`${Apiurl}project/draft/update`, payload, { headers });
      setTimeout(() => {
        onClose();
        navigate('/user/draft');
      }, 100);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update draft. Please try again.');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleClose = () => { if (!isLoading) { setError(''); onClose(); } };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      keepMounted={false}
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <SaveOutlinedIcon sx={{ fontSize: 18, color: 'warning.main' }} />
          <Typography fontWeight={700} fontSize={15}>Save Draft</Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={isLoading}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {draftId ? (
          // Already saved before — show both options
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Typography variant="body2" color="text.secondary" fontSize={12}>
              This quotation has a saved draft. Choose what to do:
            </Typography>

            {/* Update existing */}
            <Box
              onClick={!isLoading ? updateExisting : undefined}
              display="flex" alignItems="center" gap={2} p={2}
              border="1px solid" borderColor="primary.200" borderRadius={2}
              bgcolor={loadingAction === 'update' ? 'primary.100' : 'primary.50'}
              sx={{
                cursor: isLoading ? 'default' : 'pointer',
                opacity: loadingAction === 'new' ? 0.45 : 1,
                '&:hover': !isLoading ? { bgcolor: 'primary.100' } : {},
                transition: 'background 0.15s, opacity 0.15s',
                position: 'relative',
              }}
            >
              {loadingAction === 'update' ? (
                <CircularProgress size={22} sx={{ color: 'primary.main', flexShrink: 0 }} />
              ) : (
                <UpdateIcon sx={{ color: 'primary.main', fontSize: 22, flexShrink: 0 }} />
              )}
              <Box flex={1}>
                <Typography fontWeight={700} fontSize={13} color="primary.main">
                  {loadingAction === 'update' ? 'Updating…' : 'Update existing'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Overwrite the previously saved draft
                </Typography>
              </Box>
            </Box>

            {/* Save new */}
            <Box
              onClick={!isLoading ? saveNew : undefined}
              display="flex" alignItems="center" gap={2} p={2}
              border="1px solid" borderColor="grey.200" borderRadius={2}
              bgcolor={loadingAction === 'new' ? 'grey.100' : 'grey.50'}
              sx={{
                cursor: isLoading ? 'default' : 'pointer',
                opacity: loadingAction === 'update' ? 0.45 : 1,
                '&:hover': !isLoading ? { bgcolor: 'grey.100' } : {},
                transition: 'background 0.15s, opacity 0.15s',
              }}
            >
              {loadingAction === 'new' ? (
                <CircularProgress size={22} sx={{ color: 'text.secondary', flexShrink: 0 }} />
              ) : (
                <AddCircleOutlineIcon sx={{ color: 'text.secondary', fontSize: 22, flexShrink: 0 }} />
              )}
              <Box flex={1}>
                <Typography fontWeight={700} fontSize={13}>
                  {loadingAction === 'new' ? 'Saving new draft…' : 'Save as new draft'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Create a separate draft alongside the existing one
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          // First save
          <Typography variant="body2" color="text.secondary" fontSize={12}>
            Save this quotation as a draft so you can continue editing it later.
          </Typography>
        )}

        {error && (
          <Typography variant="caption" color="error" display="block" mt={1.5}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2 }}>
        <Button onClick={handleClose} disabled={isLoading} size="small" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        {/* If no prior draft, show a single Save button */}
        {!draftId && (
          <Button
            onClick={saveNew}
            disabled={isLoading}
            variant="contained"
            color="warning"
            size="small"
            startIcon={isLoading ? <CircularProgress size={13} color="inherit" /> : <SaveOutlinedIcon sx={{ fontSize: 15 }} />}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {isLoading ? 'Saving…' : 'Save Draft'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}